/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * CHON angle-bend stiffness pack, companion to mmffBondParameters. Keyed by the
 * central atom's type plus the (unordered) pair of terminal types.
 *
 * UNITS. AngularBond.compute() evaluates V = ½·strength·(θ − θ₀)² with θ in
 * radians and V in eV, so `strength` is in eV/rad². Force-field angle constants
 * are tabulated in mdyn·Å/rad²; the conversion is
 *
 *     k[eV/rad²] = k[mdyn·Å/rad²] · 6.2415     (1 mdyn·Å = 6.2415 eV)
 *
 * Note the default global angle stiffness (10 eV/rad²) is, if anything, too
 * STIFF — real CHON bends are ~2–7 eV/rad². So this pack mostly SOFTENS angles,
 * the mirror image of the bond pack (which stiffens bonds). Equilibrium angle
 * θ₀ stays at the as-built value (Regime A), so no rest energy is introduced.
 *
 * CHON-scoped stand-ins from standard angle force constants / MMFF94. Missing
 * triples fall back to the engine default via mmffParamLookup.
 */

const MDYN_A_TO_EV = 6.2415;
const k = (mdynA: number): number => mdynA * MDYN_A_TO_EV;

// Canonical key: central type, then the two terminal types sorted.
const angleKey = (center: string, endA: string, endC: string): string => {
  const [a, c] = endA <= endC ? [endA, endC] : [endC, endA];
  return `${center}|${a}~${c}`;
};

// eV/rad².
export const MMFF_LITE_ANGLE_PARAMETERS: Readonly<Record<string, number>> = {
  // ---- sp³ carbon centers (tetrahedral, ~109.5°) ----
  [angleKey('C.3', 'H', 'H')]: k(0.5),
  [angleKey('C.3', 'C.3', 'H')]: k(0.62),
  [angleKey('C.3', 'C.3', 'C.3')]: k(1.08),
  [angleKey('C.3', 'C.3', 'O.3')]: k(1.0),
  [angleKey('C.3', 'O.3', 'H')]: k(0.7),
  [angleKey('C.3', 'N.3', 'H')]: k(0.7),

  // ---- sp² carbon centers (trigonal, ~120°) ----
  [angleKey('C.2', 'C.2', 'H')]: k(0.55),
  [angleKey('C.2', 'C.2', 'C.2')]: k(0.7),
  [angleKey('C.2', 'H', 'O.2')]: k(0.7),
  [angleKey('C.2', 'C.2', 'O.2')]: k(1.0),
  [angleKey('C.2', 'H', 'H')]: k(0.5),

  // ---- aromatic carbon centers (~120°) ----
  [angleKey('C.ar', 'C.ar', 'C.ar')]: k(0.9),
  [angleKey('C.ar', 'C.ar', 'H')]: k(0.6),

  // ---- oxygen centers (bent) ----
  [angleKey('O.3', 'H', 'H')]: k(0.7), // H–O–H water ~1595 cm⁻¹ bend
  [angleKey('O.3', 'C.3', 'H')]: k(0.77),
  [angleKey('O.3', 'C.3', 'C.3')]: k(0.77),

  // ---- nitrogen centers (pyramidal) ----
  [angleKey('N.3', 'H', 'H')]: k(0.58),
  [angleKey('N.3', 'C.3', 'H')]: k(0.63),
  [angleKey('N.3', 'C.3', 'C.3')]: k(0.7),

  // ---- sp carbon centers (linear, ~180°) ----
  [angleKey('C.1', 'C.1', 'H')]: k(0.27),
  [angleKey('C.1', 'H', 'N.1')]: k(0.27),
  // O=C=O bend in CO₂ (doubly-degenerate ν₂). Tuned so the bend lands at
  // ~667 cm⁻¹ for the as-built C=O length r₀≈1.197 Å: with the harmonic angle
  // term V=½k(θ−π)², the bend eigenvalue is λ₂ = 2(k/r₀²)(1/m_O + 2/m_C).
  // See co2Spectrum.test.ts. The numerical-gradient angle force is smooth at the
  // 180° linear equilibrium (AngularBond uses central differences), so this is
  // NVE-safe despite sitting on the angle potential's degenerate plane.
  [angleKey('C.1', 'O.2', 'O.2')]: k(0.82),
};

/**
 * Look up the angle-bend stiffness (eV/rad²) for a typed triple. `center` is
 * the middle atom's type. Returns null when absent — caller keeps the default.
 */
export const lookupAngleStrength = (centerType: string, endA: string, endC: string): number | null => {
  const v = MMFF_LITE_ANGLE_PARAMETERS[angleKey(centerType, endA, endC)];
  return v === undefined ? null : v;
};
